// src/features/quoteModules/quoteModuleService.js
import { client } from "../../../api/client";

/**
 * Helpers
 */
const isLocal = () => process.env.REACT_APP_USELOCALDATA === "true";
const baseUri = process.env.REACT_APP_GATEWAY_URI;
const apiPath = "iquoteapi/quoteModule"; // maps to Route("api/[controller]") => "api/quoteModule"

/**
 * Fetch all quote modules (client-side filter by quoteRevisionId in local mode).
 * Local mode expects ./_local/_quoteModules.json with { quoteModules: [...] }.
 * Returns: { quoteModules: QuoteModule[], totalRecords: number }
 */
export const fetchQuoteModules = async (quoteRevisionId) => {
    if (isLocal()) {
        try {
            let all = require("./_local/_quoteModules.json").quoteModules || [];
            if (quoteRevisionId != null) {
                all = all.filter(r => r.quoteRevisionId === quoteRevisionId);
            }
            return all
        } catch (error) {
            console.error("Error fetching quote modules (local):", error);
            return [];
        }
    } else {
        try {
            // Controller returns full list (DeleteDate == null); paginate/filter client-side for now
            const response = await client.get(baseUri, `${apiPath}/ByQuoteRevisionId/${quoteRevisionId}`);
            let all = Array.isArray(response.data) ? response.data : [];

            // Convert JSON fields to objects/arrays
            all = all.map(x => {
                let parsedInputs, parsedWarnings;

                try {
                    parsedInputs = JSON.parse(x.inputValues);
                } catch {
                    parsedInputs = [];
                }

                try {
                    parsedWarnings = JSON.parse(x.warnings);
                } catch {
                    parsedWarnings = [];
                }

                return {
                    ...x,
                    inputValues: Array.isArray(parsedInputs) ? parsedInputs : [],
                    warnings: Array.isArray(parsedWarnings) ? parsedWarnings : []
                };
            });

            console.log("Fetched quote modules:", all);

            return all
        } catch (error) {
            console.error("Error fetching quote modules:", error);
            return [];
        }
    }
};

/**
 * Create a quote module
 * Controller: POST api/quoteModule (accepts QuoteModuleDTO)
 * Returns: server response (201) or a mock with newId in local
 */
export const createQuoteModule = async (quoteModule) => {
    if (isLocal()) {
        return { ...quoteModule, id: Math.floor(Math.random() * 100000) };
    } else {
        try {
            var body = structuredClone(quoteModule);

            /*
                Convert fields that need to be provided in JSON format
            */
            body.inputValues = JSON.stringify(body.inputValues, null, 2)
            body.warnings = JSON.stringify(body.warnings, null, 2)

            /*
                Save to DB
            */

            var result = await client.post(baseUri, `${apiPath}`, body);

            var savedQuoteModule = result.data.quoteModule

            // Convert JSON fields to objects/arrays
            savedQuoteModule.inputValues = JSON.parse(savedQuoteModule.inputValues)
            savedQuoteModule.warnings = JSON.parse(savedQuoteModule.warnings)

            return savedQuoteModule;
        } catch (error) {
            console.error("Error creating quote module:", error);
            return [];
        }
    }
};

/**
 * Update a quote module
 * Controller: PUT api/quoteModule/{id} (accepts QuoteModuleDTO)
 */
export const updateQuoteModule = async (quoteModule) => {
    if (isLocal()) {
        return { data: { success: true } };
    } else {
        try {
            var body = structuredClone(quoteModule);

            // Convert JSON fields to objects/arrays
            body.inputValues = JSON.stringify(body.inputValues, null, 2)
            body.warnings = JSON.stringify(body.warnings, null, 2)

            return await client.put(baseUri, `${apiPath}/${body.id}`, body);
        } catch (error) {
            console.error("Error updating quote module:", error);
            return [];
        }
    }
};

/**
 * Delete (soft delete) a quote module
 * Controller: DELETE api/quoteModule/{id}
 */
export const deleteQuoteModule = async (quoteModule) => {
    if (isLocal()) {
        return { data: { success: true } };
    } else {
        try {
            return await client.delete(baseUri, `${apiPath}/${quoteModule.id}`);
        } catch (error) {
            if (error?.response?.status === 404) {
                return { data: { notFound: true } };
            }
            console.error("Error deleting quote module:", error);
            return [];
        }
    }
};

export const decoupleModule = async (quoteModuleGuid, revisionId) => {
    if (isLocal()) {
        return { data: { success: true } };
    }
    else {
        try {
            return await client.get(baseUri, `iquoteapi/quoteModule/decoupleModule/${quoteModuleGuid}`);
        }
        catch (error) {
            if (error?.response?.status === 404) {
                return { data: { notFound: true } };
            }
            console.error("Error deleting quote module:", error);
            return [];
        }
    }
}